import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Workflow, GripVertical, Plus, Trash2, RotateCcw, History as HistoryIcon, Save, ChevronLeft, ArrowRight, Copy, Check } from "lucide-react";
import * as Tabs from "@radix-ui/react-tabs";
import { BridgeApi, RevisionsApi, AuthApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { Button, Pill, EmptyState, CenteredSpinner, ErrorBanner } from "../components/primitives";
import { AclBadge, PlatformBadge, PlatformTag, SinkBadge } from "../components/badges";
import { JsonDiff } from "../components/JsonDiff";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/cn";
import type { Platform, Route } from "../lib/types";

const CHAT_KINDS = ["dm", "group", "newsletter", "broadcast", "unknown"];

const NEW_ROUTE: Route = {
  name: "",
  match: {},
  acl: { inbound: "allow" },
  persona: "",
  sink_type: "persona",
  behavior: { debounce_ms: 8000, max_defer_ms: 60000, history_limit: 10, wake_mode: "all" },
};

// The behavioral "kind" of a route — how it engages — derived from its sink +
// behavior. Drives the type filter and lets a maintainer group at a glance.
type RouteKind = "observer" | "lurker" | "reply" | "notification" | "webhook" | "script";
function routeKind(r: Route): RouteKind {
  const sink = r.sink_type ?? "persona";
  if (sink !== "persona") return sink as RouteKind;       // notification | webhook | script
  if (r.behavior.observe) return "observer";              // silent, gathers memory
  if (r.behavior.wake_mode === "mention") return "lurker"; // replies only on mention/recent
  return "reply";                                          // replies on every batch
}
const KIND_LABEL: Record<RouteKind, string> = {
  observer: "observer", lurker: "lurker", reply: "reply",
  notification: "notification", webhook: "webhook", script: "script",
};

// Sort dimensions. "position" is the canonical top-down evaluation order (deny
// routes firewall everything below them), and is the ONLY mode where drag-to-
// reorder is meaningful — any other sort is a read-only view, so reorder pauses.
type SortKey = "position" | "channel" | "persona" | "name" | "kind";
const SORT_LABEL: Record<SortKey, string> = {
  position: "Order (eval)", channel: "Channel", persona: "Persona", name: "Name A–Z", kind: "Type",
};

export default function Routes() {
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<string>("all"); // by channel name
  const [personaFilter, setPersonaFilter] = useState<string>("all");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortKey>("position");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [orderDirty, setOrderDirty] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  const { data: cfg, isLoading, error } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });

  const routes = useMemo(() => {
    if (!cfg) return [] as Route[];
    let list = [...cfg.routes];
    if (pendingOrder) {
      const byName = new Map(list.map((r) => [r.name, r]));
      list = pendingOrder.map((n) => byName.get(n)).filter((r): r is Route => !!r);
      // Keep any extra routes appended (defensive)
      for (const r of cfg.routes) if (!pendingOrder.includes(r.name)) list.push(r);
    }
    return list;
  }, [cfg, pendingOrder]);

  // Channel filter lists EVERY channel (from config), not only ones that
  // already have routes — so a channel with zero routes (e.g. a freshly added
  // slack channel) still shows up, annotated with its route count.
  const channelOptions = useMemo(() => (cfg?.channels ?? []).map((c) => c.name).sort(), [cfg]);
  const routeCountByChannel = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of routes) if (r.channel_name) m.set(r.channel_name, (m.get(r.channel_name) ?? 0) + 1);
    return m;
  }, [routes]);
  const personaOptions = useMemo(
    () => [...new Set(routes.map((r) => r.persona).filter((p): p is string => !!p))].sort(),
    [routes],
  );
  const kindOptions = useMemo(
    () => [...new Set(routes.map(routeKind))].sort((a, b) => KIND_LABEL[a].localeCompare(KIND_LABEL[b])),
    [routes],
  );

  const matched = routes.filter((r) => {
    if (filter !== "all" && r.channel_name !== filter) return false;
    if (personaFilter !== "all" && r.persona !== personaFilter) return false;
    if (kindFilter !== "all" && routeKind(r) !== kindFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      r.name.toLowerCase().includes(q) ||
      (r.channel_name ?? "").toLowerCase().includes(q) ||
      (r.chat_name ?? "").toLowerCase().includes(q) ||
      (r.match.chat_jid ?? "").toLowerCase().includes(q) ||
      (r.persona ?? "").toLowerCase().includes(q)
    );
  });

  const filtersActive = filter !== "all" || personaFilter !== "all" || kindFilter !== "all" || search.trim() !== "";
  // Drag-to-reorder is only valid in the canonical full-order view: no filters
  // and sort=position. Any filter or sort turns this into a read-only observe
  // view (matching subset, sorted) and pauses reordering.
  const reorderMode = !filtersActive && sortBy === "position";
  const pos = useMemo(() => new Map(routes.map((r, i) => [r.name, i])), [routes]);
  const displayList = reorderMode
    ? routes
    : [...matched].sort((a, b) => {
        const byPos = (pos.get(a.name) ?? 0) - (pos.get(b.name) ?? 0);
        switch (sortBy) {
          case "name": return a.name.localeCompare(b.name) || byPos;
          case "channel": return (a.channel_name ?? "~").localeCompare(b.channel_name ?? "~") || byPos;
          case "persona": return (a.persona ?? "~").localeCompare(b.persona ?? "~") || byPos;
          case "kind": return KIND_LABEL[routeKind(a)].localeCompare(KIND_LABEL[routeKind(b)]) || byPos;
          default: return byPos; // position
        }
      });
  const clearFilters = () => { setFilter("all"); setPersonaFilter("all"); setKindFilter("all"); setSearch(""); setSortBy("position"); };

  const reorderMut = useMutation({
    mutationFn: BridgeApi.reorderRoutes,
    onSuccess: () => {
      setOrderDirty(false);
      setPendingOrder(null);
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      toast.success("Order saved");
    },
    onError: (err) => toast.error("Reorder failed", String(err)),
  });

  const deleteMut = useMutation({
    // True delete (Trash button) → clear any assist refs into this route so they
    // don't dangle. (The rename path deletes via BridgeApi directly, without this.)
    mutationFn: (name: string) => BridgeApi.deleteRoute(name, { clearAssistRefs: true }),
    onSuccess: (res, name) => {
      const cleared = (res?.clearedRoutes ?? 0) + (res?.clearedChannels ?? 0);
      toast.success(`Route "${name}" deleted`, cleared > 0 ? `${cleared} assist reference(s) reset to none.` : undefined);
      setSelected(null);
      qc.invalidateQueries({ queryKey: qk.bridge.config });
    },
    onError: (err) => toast.error("Delete failed", String(err)),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    if (!reorderMode) return; // reordering only in the canonical full-order view
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    // reorderMode guarantees displayList === full route list in evaluation order,
    // so we reorder against the complete name list (positions are global).
    const baseNames = routes.map((r) => r.name);
    const oldIdx = baseNames.indexOf(String(active.id));
    const newIdx = baseNames.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(baseNames, oldIdx, newIdx);
    setPendingOrder(next);
    setOrderDirty(true);
  };

  const personaNames = cfg?.personas ? Object.keys(cfg.personas) : [];
  const selectedRoute = selected ? routes.find((r) => r.name === selected) ?? null : null;
  const isCreating = selected === "__new__";
  // Mobile is single-pane (Routes has its own layout, not the shared
  // ListDetail): show the list, or the detail full-width when one's open.
  const detailOpen = !!selectedRoute || isCreating;

  if (isLoading) return <CenteredSpinner />;
  if (error) return <PageBody><ErrorBanner error={error as Error} /></PageBody>;
  if (!cfg) return null;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Routes"
        icon={Workflow}
        subtitle={`${cfg.routes.length} routes · default ${cfg.default.acl.inbound} · evaluated top-down`}
        actions={
          <>
            {orderDirty && (
              <>
                <Button size="sm" variant="ghost" onClick={() => { setPendingOrder(null); setOrderDirty(false); }}>
                  Discard reorder
                </Button>
                <Button size="sm" onClick={() => pendingOrder && reorderMut.mutate(pendingOrder)} loading={reorderMut.isPending}>
                  <Save className="size-3.5" />
                  Save order
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => setSelected("__new__")}>
              <Plus className="size-3.5" />
              New route
            </Button>
          </>
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        {/* Left list — full width on mobile, fixed on desktop; hidden on
            mobile while a detail/create view is open. */}
        <div className={cn(
          "flex-col min-h-0 md:flex-shrink-0 border-r border-border w-full md:w-[28rem]",
          detailOpen ? "hidden md:flex" : "flex",
        )}>
          <div className="border-b border-border px-3 py-2 space-y-2">
            <input
              placeholder="search name · chat · persona…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs w-full"
            />
            {/* 2-col grid: symmetric + graceful-shrink on both the fixed 28rem
                desktop pane and the full-width mobile pane (flex-wrap packed 3+1
                on desktop and could overflow sub-320px). min-w-0 lets selects
                shrink below their content so they never push past the pane. */}
            <div className="grid grid-cols-2 gap-1.5">
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-7 text-xs w-full min-w-0" title="Filter by channel">
                <option value="all">all channels</option>
                {channelOptions.map((c) => <option key={c} value={c}>{c} ({routeCountByChannel.get(c) ?? 0})</option>)}
              </select>
              <select value={personaFilter} onChange={(e) => setPersonaFilter(e.target.value)} className="h-7 text-xs w-full min-w-0" title="Filter by persona">
                <option value="all">all personas</option>
                {personaOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="h-7 text-xs w-full min-w-0" title="Filter by type">
                <option value="all">all types</option>
                {kindOptions.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)} className="h-7 text-xs w-full min-w-0" title="Sort by">
                {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => <option key={k} value={k}>↕ {SORT_LABEL[k]}</option>)}
              </select>
            </div>
            {(filtersActive || sortBy !== "position") && (
              <div className="flex items-center justify-between gap-2 text-[11px] text-fg-subtle">
                <span className="min-w-0">
                  {displayList.length} of {routes.length}
                  {sortBy !== "position" && <> · sorted by {SORT_LABEL[sortBy].toLowerCase()}</>}
                  {!reorderMode && <> · <span className="text-warning">reorder paused</span></>}
                </span>
                <button type="button" onClick={clearFilters} className="underline hover:text-fg flex-shrink-0">clear</button>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {displayList.length === 0 ? (
              <EmptyState icon={<Workflow className="size-8" />} title="No routes match" />
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={displayList.map((r) => r.name)} strategy={verticalListSortingStrategy}>
                  {displayList.map((r) => (
                    <SortableRouteRow
                      key={r.name}
                      route={r}
                      draggable={reorderMode}
                      active={selected === r.name}
                      onClick={() => setSelected(r.name)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        {/* Detail pane — hidden on mobile until a route is selected/created. */}
        <div className={cn(
          "flex-1 min-w-0 flex-col bg-app",
          detailOpen ? "flex" : "hidden md:flex",
        )}>
          {detailOpen && (
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="md:hidden flex items-center gap-1 px-3 py-2.5 border-b border-border text-sm text-fg-muted hover:text-fg bg-surface flex-shrink-0 ring-focus"
            >
              <ChevronLeft className="size-4" />
              Back
            </button>
          )}
          {selectedRoute || isCreating ? (
            <RouteEditor
              route={selectedRoute ?? NEW_ROUTE}
              isCreating={isCreating}
              personaNames={personaNames}
              mcpByPersona={cfg.mcp_servers_by_persona ?? {}}
              allRoutes={cfg.routes}
              existingNames={cfg.routes.map((r) => r.name)}
              onSaved={(name) => {
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                setSelected(name);
              }}
              onDelete={() => {
                if (!selectedRoute) return;
                // Warn if anything reports help-opportunities into this route — those
                // refs will be reset to "none" (the server clears them on delete).
                const refRoutes = cfg.routes.filter((r) => r.assist_report_to === selectedRoute.name).length;
                const refChannels = (cfg.channels ?? []).filter((ch) => ch.default_assist_report_to === selectedRoute.name).length;
                const warn = (refRoutes || refChannels)
                  ? `\n\n⚠ ${refRoutes} route(s) and ${refChannels} channel(s) report help-opportunities into it — those will be reset to “none”.`
                  : "";
                if (confirm(`Delete route "${selectedRoute.name}"?${warn}`)) {
                  deleteMut.mutate(selectedRoute.name);
                }
              }}
              onClose={() => setSelected(null)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={<Workflow className="size-8" />}
                title="Select a route"
                description="Click a row to edit, drag the handle to reorder."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sortable row ───────────────────────────────────────────────────────

/** Tiny copy-the-JID affordance shown beside a resolved chat name (the JID is
 *  otherwise hidden). Stops click propagation so it doesn't open the editor. */
function CopyJid({ jid }: { jid: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={`Copy JID: ${jid}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(jid).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        }).catch(() => { /* clipboard blocked */ });
      }}
      className="p-0.5 rounded text-fg-subtle hover:text-fg hover:bg-surface-2 transition-colors flex-shrink-0"
    >
      {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
    </button>
  );
}

function SortableRouteRow({
  route,
  active,
  draggable,
  onClick,
}: {
  route: Route;
  active: boolean;
  /** Whether drag-to-reorder is live (only in the canonical full-order view).
   *  When false the grip is hidden and the row is click-only. */
  draggable: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: route.name, disabled: !draggable });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const isDeny = route.acl.inbound === "deny";
  return (
    <div ref={setNodeRef} style={style} className={cn("border-b border-border", isDragging && "bg-surface-2")}>
      {/* deny routes firewall everything below them — mark with a red rail + tint */}
      <div className={cn("flex items-stretch border-l-2", isDeny ? "border-l-danger bg-danger-subtle/20" : "border-l-transparent", active && "bg-accent-subtle/40")}>
        {draggable ? (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="px-1 text-fg-subtle hover:text-fg cursor-grab active:cursor-grabbing flex items-center"
            aria-label="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="size-3.5" />
          </button>
        ) : (
          <span className="px-1 w-[1.375rem] flex-shrink-0" aria-hidden />
        )}
        <button type="button" onClick={onClick} className="flex-1 text-left px-2 py-2.5 ring-focus min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-fg truncate">{route.name}</span>
            <AclBadge acl={route.acl.inbound} />
            {route.behavior.observe
              ? <Pill tone="neutral">👁 observer</Pill>
              : route.behavior.wake_mode === "mention" && <Pill tone="info">lurker</Pill>}
          </div>
          {/* match  →  sink */}
          <div className="flex items-center gap-2 text-xs min-w-0">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {route.match.platform && <PlatformTag platform={route.match.platform} />}
              {route.channel_name && <span className="text-[11px] text-fg-subtle whitespace-nowrap flex-shrink-0">{route.channel_name}</span>}
              <span className="text-fg-subtle flex-shrink-0">·</span>
              {route.chat_name ? (
                <>
                  <span className="text-fg truncate">{route.chat_name}</span>
                  {route.match.chat_jid && <CopyJid jid={route.match.chat_jid} />}
                </>
              ) : (
                <span className="font-mono text-fg-muted truncate">{route.match.chat_jid ?? "(any chat)"}</span>
              )}
            </div>
            <ArrowRight className="size-3.5 text-fg-subtle flex-shrink-0" />
            <div className="flex-shrink-0">
              <SinkBadge sinkType={route.sink_type} persona={route.persona} />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── Route editor ───────────────────────────────────────────────────────

function RouteEditor({
  route,
  isCreating,
  personaNames,
  mcpByPersona,
  allRoutes,
  existingNames,
  onSaved,
  onDelete,
  onClose,
}: {
  route: Route;
  isCreating: boolean;
  personaNames: string[];
  mcpByPersona: Record<string, string[]>;
  allRoutes: Route[];
  existingNames: string[];
  onSaved: (name: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<Route>(route);
  const [tab, setTab] = useState<"edit" | "diff" | "history">("edit");

  // Reset draft when target route changes
  useEffect(() => { setDraft(route); }, [route.name, isCreating]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(route),
    [draft, route],
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isCreating) {
        if (!draft.name) throw new Error("name is required");
        if (existingNames.includes(draft.name)) throw new Error(`route "${draft.name}" already exists`);
        await BridgeApi.saveRoute(draft.name, draft);
      } else {
        // If renamed, delete old then create new
        if (draft.name !== route.name) {
          await BridgeApi.saveRoute(draft.name, draft);
          await BridgeApi.deleteRoute(route.name);
        } else {
          await BridgeApi.saveRoute(draft.name, draft);
        }
      }
    },
    onSuccess: () => {
      toast.success(`Route "${draft.name}" saved`);
      onSaved(draft.name);
    },
    onError: (err) => toast.error("Save failed", String(err)),
  });

  return (
    <Tabs.Root value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="flex-1 flex flex-col min-h-0">
      <div className="px-5 pt-4 pb-2 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-fg">{isCreating ? "New route" : route.name}</h2>
            {!isCreating && draft.match.platform && <PlatformBadge platform={draft.match.platform as Platform} />}
            {!isCreating && route.channel_name && <Pill tone="info">{route.channel_name}</Pill>}
            {!isCreating && <AclBadge acl={draft.acl.inbound} />}
          </div>
          <p className="text-xs text-fg-muted mt-1">{isCreating ? "Define a new chat → persona route." : `→ ${route.persona}`}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!isCreating && (
            <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete">
              <Trash2 className="size-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">✕</Button>
        </div>
      </div>

      <Tabs.List className="border-b border-border px-5 py-1.5 flex items-center gap-3 text-xs font-medium">
        <TabBtn value="edit">Edit</TabBtn>
        <TabBtn value="diff" disabled={!dirty}>Diff {dirty && <Pill tone="warning" className="ml-1">unsaved</Pill>}</TabBtn>
        {!isCreating && <TabBtn value="history">History</TabBtn>}
      </Tabs.List>

      <div className="flex-1 overflow-y-auto">
        <Tabs.Content value="edit" className="px-5 py-4 space-y-4 outline-none">
          <RouteFields draft={draft} setDraft={setDraft} personaNames={personaNames} mcpByPersona={mcpByPersona} allRoutes={allRoutes} />
        </Tabs.Content>

        <Tabs.Content value="diff" className="px-5 py-4 outline-none">
          <div className="text-xs text-fg-muted mb-2">Will be written to <code className="font-mono">routes.yaml</code> on save:</div>
          <JsonDiff before={route} after={draft} />
        </Tabs.Content>

        {!isCreating && (
          <Tabs.Content value="history" className="px-5 py-4 outline-none">
            <RouteHistory routeName={route.name} onReverted={() => onSaved(route.name)} />
          </Tabs.Content>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
        {dirty && <span className="mr-auto text-xs text-warning">unsaved changes</span>}
        {!dirty && !isCreating && <span className="mr-auto text-xs text-fg-subtle">in sync</span>}
        {tab !== "diff" && (
          <Button size="sm" variant="secondary" onClick={() => setTab("diff")} disabled={!dirty}>Preview diff</Button>
        )}
        <Button size="sm" onClick={() => saveMut.mutate()} disabled={!dirty || !draft.name} loading={saveMut.isPending}>
          <Save className="size-3.5" />
          {isCreating ? "Create" : "Save"}
        </Button>
      </div>
    </Tabs.Root>
  );
}

function TabBtn({ value, children, disabled }: { value: string; children: React.ReactNode; disabled?: boolean }) {
  return (
    <Tabs.Trigger
      value={value}
      disabled={disabled}
      className={cn(
        "h-7 px-2 rounded text-fg-muted hover:text-fg data-[state=active]:text-fg data-[state=active]:bg-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1",
      )}
    >
      {children}
    </Tabs.Trigger>
  );
}

// ─── Route fields ───────────────────────────────────────────────────────

export function RouteFields({
  draft,
  setDraft,
  personaNames,
  mcpByPersona,
  allRoutes,
}: {
  draft: Route;
  setDraft: (r: Route) => void;
  personaNames: string[];
  mcpByPersona: Record<string, string[]>;
  allRoutes: Route[];
}) {
  const update = (patch: Partial<Route>) => setDraft({ ...draft, ...patch });
  const updateMatch = (patch: Partial<Route["match"]>) => setDraft({ ...draft, match: { ...draft.match, ...patch } });
  const updateBehavior = (patch: Partial<Route["behavior"]>) =>
    setDraft({ ...draft, behavior: { ...draft.behavior, ...patch } });

  // A route is scoped to a channel (which fixes its platform). Picking a
  // channel sets channel_id (what the save resolves on) + the derived platform.
  const { data: channels = [] } = useQuery({
    queryKey: ["bridge", "channels"],
    queryFn: async () => {
      const r = await fetch("/api/bridge/channels");
      return r.ok ? (r.json() as Promise<Array<{ id: number; name: string; platform: Platform }>>) : [];
    },
  });
  // Assist-session FSM state. When on, an observer may report into a route owned by
  // ANOTHER persona (that route's persona becomes the broker) — so the report-to
  // picker must offer cross-persona targets. When off, only same-persona resolves.
  const { data: assist } = useQuery({ queryKey: ["assist-sessions"], queryFn: AuthApi.assistSessions });
  const assistFsm = assist?.enabled ?? false;
  const selectChannel = (name: string) => {
    const ch = channels.find((c) => c.name === name);
    if (!ch) return;
    setDraft({
      ...draft,
      channel_id: ch.id,
      channel_name: ch.name,
      channel_platform: ch.platform,
      match: { ...draft.match, platform: ch.platform, channel: ch.name },
    });
  };

  return (
    <div className="space-y-4">
      <FieldGroup title="Identity">
        <Field label="Route name">
          <input value={draft.name} onChange={(e) => update({ name: e.target.value })} className="w-full" placeholder="e.g. harry-dm" />
        </Field>
        <Field label="Sink (where this route ends)">
          <select
            value={draft.sink_type ?? "persona"}
            onChange={(e) => update({ sink_type: e.target.value as NonNullable<Route["sink_type"]> })}
            className="w-full"
          >
            <option value="persona">persona — spawn an agent</option>
            <option value="notification">notification — outbound alert target (no persona)</option>
          </select>
          {draft.sink_type === "notification" && (
            <p className="text-[11px] text-fg-subtle mt-1">
              Point this at a dedicated chat. The watchdog and other notices deliver here — no agent runs, so nothing is woken or polluted.
            </p>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          {(draft.sink_type ?? "persona") === "persona" ? (
            <Field label="Persona">
              <select value={draft.persona ?? ""} onChange={(e) => update({ persona: e.target.value })} className="w-full">
                <option value="">(choose)</option>
                {personaNames.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="Persona">
              <div className="text-xs text-fg-subtle py-1.5">— (none; this is a {draft.sink_type} sink)</div>
            </Field>
          )}
          <Field label="ACL inbound">
            <select
              value={draft.acl.inbound}
              onChange={(e) => update({ acl: { inbound: e.target.value as Route["acl"]["inbound"] } })}
              className="w-full"
            >
              <option value="allow">allow</option>
              <option value="deny">deny</option>
              <option value="forward_only">forward_only</option>
            </select>
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup title="Match">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Channel">
            <select
              value={draft.channel_name ?? ""}
              onChange={(e) => selectChannel(e.target.value)}
              className="w-full"
            >
              <option value="">(choose channel)</option>
              {channels.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Chat kind">
            <select
              value={(draft.match.chat_kind as string) ?? ""}
              onChange={(e) => updateMatch({ chat_kind: (e.target.value || undefined) as Route["match"]["chat_kind"] })}
              className="w-full"
            >
              <option value="">(any)</option>
              {CHAT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Chat JID (exact)">
          <input
            value={draft.match.chat_jid ?? ""}
            onChange={(e) => updateMatch({ chat_jid: e.target.value || undefined })}
            className="w-full font-mono text-xs"
            placeholder="e.g. 1234567890@s.whatsapp.net"
          />
        </Field>
        <label className="flex items-center gap-2 text-xs text-fg-muted cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(draft.match.mention_required)}
            onChange={(e) => updateMatch({ mention_required: e.target.checked })}
            className="size-3.5"
          />
          Require mention of persona's mention_names
        </label>
        <Field label="Sender allowlist (comma-separated JIDs, optional)">
          <input
            value={(draft.match.sender_allowlist ?? []).join(", ")}
            onChange={(e) =>
              updateMatch({
                sender_allowlist: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            className="w-full font-mono text-xs"
            placeholder="optional"
          />
        </Field>
      </FieldGroup>

      <FieldGroup title="Behavior">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Debounce (ms)">
            <input
              type="number"
              value={draft.behavior.debounce_ms}
              onChange={(e) => updateBehavior({ debounce_ms: parseInt(e.target.value, 10) || 0 })}
              className="w-full"
            />
          </Field>
          <Field label="Max defer (ms)">
            <input
              type="number"
              value={draft.behavior.max_defer_ms}
              onChange={(e) => updateBehavior({ max_defer_ms: parseInt(e.target.value, 10) || 0 })}
              className="w-full"
            />
          </Field>
          <Field label="History limit">
            <input
              type="number"
              value={draft.behavior.history_limit}
              onChange={(e) => updateBehavior({ history_limit: parseInt(e.target.value, 10) || 0 })}
              className="w-full"
            />
          </Field>
        </div>
        {(draft.sink_type ?? "persona") === "persona" && (
          <Field label="Engagement">
            <select
              value={draft.behavior.observe ? "observe" : (draft.behavior.wake_mode ?? "all")}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "observe") updateBehavior({ observe: true });
                else updateBehavior({ observe: false, wake_mode: v as "all" | "mention" });
              }}
              className="w-full"
            >
              <option value="all">all — reply on every batch</option>
              <option value="mention">mention — lurk; reply on mention, reply, or recent engagement</option>
              <option value="observe">observe — silent; never reply, just gather memory</option>
            </select>
            <p className="text-[11px] text-fg-subtle mt-1">
              {draft.behavior.observe
                ? "Silent observer: NEVER replies. Contacts + journal are captured passively, and a periodic sweep feeds new messages into this persona's memory."
                : "Lurker still journals every message — it only skips the spawn. Wakes when named, replied to, or active within 10 min."}
            </p>
          </Field>
        )}
        {(draft.sink_type ?? "persona") === "persona" && draft.behavior.observe && (() => {
          // With the assist FSM on, a cross-persona report route is valid (its persona
          // brokers); without it, only same-persona resolves in observeRoutes().
          const targets = allRoutes.filter((r) => r.name !== draft.name && !r.behavior?.observe && (assistFsm || r.persona === draft.persona));
          const cur = draft.assist_report_to ?? "";
          // A stored target that isn't a valid option (renamed/deleted route, or
          // wrong persona/observer) would otherwise render as a silent "none".
          const dangling = !!cur && !targets.some((r) => r.name === cur);
          const notFound = !!cur && !allRoutes.some((r) => r.name === cur);
          return (
            <Field label="Assist — report help-opportunities to">
              <select
                value={cur}
                onChange={(e) => update({ assist_report_to: e.target.value || null })}
                className={cn("w-full", dangling && "ring-1 ring-warning")}
              >
                <option value="">none — pure silent observer (memory only)</option>
                {targets.map((r) => <option key={r.name} value={r.name}>{r.name}{r.persona !== draft.persona ? ` (${r.persona} brokers)` : ""}{r.chat_name ? ` — ${r.chat_name}` : ""}</option>)}
                {dangling && <option value={cur}>{cur} — ⚠ {notFound ? "route not found" : "invalid target"}</option>}
              </select>
              {dangling ? (
                <p className="text-[11px] text-warning mt-1">⚠ <span className="font-mono">{cur}</span> {notFound ? "is not an existing route" : "isn’t a valid target for this persona"} — assist won’t fire. Pick a real target (or none).</p>
              ) : (
                <p className="text-[11px] text-fg-subtle mt-1">
                  {cur
                    ? <>Assist-observer: each memory sweep also looks for clear, high-value help. When it finds some, <span className="font-mono">{draft.persona}</span> proposes it in <span className="font-mono">{cur}</span>; on your “yes” there, it posts the result to THIS chat. (Tip: scope its tools here with the MCP servers section below.)</>
                    : <>Pure observer: silently reads + builds memory, never reaches out. Pick a chat (e.g. your DM with {draft.persona || "the persona"}) to turn this into a help-offering assistant.</>}
                </p>
              )}
              {!cur && targets.length === 0 && (
                <p className="text-[11px] text-warning mt-1">This persona has no non-observer route to report into — create a DM route first.</p>
              )}
            </Field>
          );
        })()}
        {(draft.sink_type ?? "persona") === "persona" && !draft.behavior.observe && (
          <Field label="Proactivity here">
            <div className="grid grid-cols-2 gap-2">
              <select value={draft.proactive ?? "inherit"} onChange={(e) => update({ proactive: e.target.value as NonNullable<Route["proactive"]> })} className="w-full">
                <option value="inherit">inherit persona default</option>
                <option value="on">enable here</option>
                <option value="off">disable here</option>
              </select>
              <select value={draft.proactive_mode ?? "inherit"} onChange={(e) => update({ proactive_mode: e.target.value as NonNullable<Route["proactive_mode"]> })} className="w-full" disabled={(draft.proactive ?? "inherit") === "off"}>
                <option value="inherit">mode: inherit</option>
                <option value="ask">mode: ask first</option>
                <option value="auto">mode: autonomous</option>
              </select>
            </div>
            <p className="text-[11px] text-fg-subtle mt-1">
              Only takes effect when <span className="font-mono">{draft.persona || "the persona"}</span>'s Proactivity master switch is ON (Personas page). “enable here” opts this chat in even if the persona is set to opt-in-only.
            </p>
          </Field>
        )}
        {(draft.sink_type ?? "persona") === "persona" && (
          <Field label="Extra instructions for this chat">
            <textarea
              value={draft.instructions ?? ""}
              onChange={(e) => update({ instructions: e.target.value || null })}
              rows={4}
              className="w-full font-sans text-sm"
              placeholder="Standing guidance for the persona in THIS chat — e.g. 'This is the FO team; escalate refunds to plutus. Reply in formal Bahasa here.'"
            />
            <p className="text-[11px] text-fg-subtle mt-1">
              Injected into the persona's prompt on top of its SOUL + memories, every time it's spawned for this chat (reply or observer sweep). Keep it concise. Applies whether or not this is an observer.
            </p>
          </Field>
        )}
      </FieldGroup>

      {(draft.sink_type ?? "persona") === "persona" && draft.persona && (
        <FieldGroup title="MCP servers">
          <RouteMcpPicker
            available={mcpByPersona[draft.persona] ?? []}
            persona={draft.persona}
            selected={draft.mcp_servers ?? null}
            onChange={(mcp_servers) => update({ mcp_servers })}
          />
        </FieldGroup>
      )}
    </div>
  );
}

/** Per-route MCP server allowlist. `null` selected = all available servers (the
 *  default — unchanged behavior). Unchecking stores the explicit reduced list;
 *  re-checking everything collapses back to `null`. The `agent-fleet` bus server
 *  is always available to the spawn and is intentionally not listed here. Reused on
 *  the Channels page (`scope="channel"`) to set the default inherited by auto-routed
 *  observer routes. */
export function RouteMcpPicker({
  available, persona, selected, onChange, scope = "route",
}: {
  available: string[];
  persona: string;
  selected: string[] | null;
  onChange: (next: string[] | null) => void;
  scope?: "route" | "channel";
}) {
  if (available.length === 0) {
    return (
      <p className="text-[11px] text-fg-subtle">
        <span className="font-mono">{persona}</span> has no MCP servers granted (on the <span className="font-mono">MCP</span> page) — nothing to scope. Grant a connector there first, then withhold it here if you want.
      </p>
    );
  }
  const all = selected == null;
  const allowed = new Set(all ? available : selected);
  const toggle = (s: string, on: boolean) => {
    const next = new Set(allowed);
    if (on) next.add(s); else next.delete(s);
    const arr = available.filter((x) => next.has(x));
    onChange(arr.length === available.length ? null : arr);  // all → null (clean default)
  };
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] text-fg-subtle">
        {scope === "channel" ? (
          <>Which of <span className="font-mono">{persona}</span>'s <span className="font-mono">/mcp</span>-granted servers auto-created observer routes on this channel may use. Uncheck to withhold one from every new chat here. All-checked = default (every granted server). The <span className="font-mono">MCP</span> page sets the persona-wide grant; this only narrows it for this channel's auto-routes.</>
        ) : (
          <>Which of <span className="font-mono">{persona}</span>'s <span className="font-mono">/mcp</span>-granted servers spawns on THIS route may use. Uncheck to withhold one on this chat only (e.g. <span className="font-mono">composio</span> in a DM but not a group). All-checked = default (every granted server). The <span className="font-mono">MCP</span> page sets the persona-wide grant; this only narrows it per route.</>
        )}
      </p>
      {available.map((s) => (
        <label key={s} className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" className="size-3.5" checked={allowed.has(s)} onChange={(e) => toggle(s, e.target.checked)} />
          <span className="font-mono">{s}</span>
        </label>
      ))}
      <p className="text-[11px] text-fg-subtle">
        {all ? "All servers allowed (default)." : `Scoped — ${allowed.size}/${available.length} server(s) allowed on ${scope === "channel" ? "this channel's auto-routes" : "this route"}.`}
      </p>
    </div>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="panel px-3 pt-3 pb-3.5 space-y-3">
      <legend className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle px-1">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">{label}</span>
      {children}
    </label>
  );
}

// ─── Route history ─────────────────────────────────────────────────────

function RouteHistory({ routeName, onReverted }: { routeName: string; onReverted: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: revs = [], isLoading } = useQuery({
    queryKey: qk.revisions.list({ object_type: "route", object_name: routeName }),
    queryFn: () => RevisionsApi.list({ object_type: "route", object_name: routeName, limit: 50 }),
  });
  const revert = useMutation({
    mutationFn: RevisionsApi.revert,
    onSuccess: () => {
      toast.success("Reverted");
      qc.invalidateQueries({ queryKey: qk.bridge.config });
      qc.invalidateQueries({ queryKey: qk.revisions.list({ object_type: "route", object_name: routeName }) });
      onReverted();
    },
    onError: (err) => toast.error("Revert failed", String(err)),
  });

  if (isLoading) return <CenteredSpinner />;
  if (revs.length === 0) {
    return (
      <div className="text-sm text-fg-muted py-6 text-center">
        <HistoryIcon className="size-6 mx-auto mb-2 text-fg-subtle" />
        No prior revisions.
      </div>
    );
  }

  return (
    <ul className="panel divide-y divide-border">
      {revs.map((r) => {
        const before = r.before ? JSON.parse(r.before) : null;
        const after = r.after ? JSON.parse(r.after) : null;
        return (
          <li key={r.id} className="p-3 text-xs">
            <div className="flex items-center gap-2">
              <Pill tone={r.action === "delete" ? "danger" : r.action === "create" ? "success" : "warning"}>
                {r.action}
              </Pill>
              <span className="text-fg-muted">{relativeTime(r.at)}</span>
              <span className="ml-auto font-mono text-fg-subtle">by {r.actor}</span>
            </div>
            <details className="mt-2">
              <summary className="text-fg-muted cursor-pointer hover:text-fg">show diff</summary>
              <div className="mt-2">
                <JsonDiff before={before} after={after} />
              </div>
            </details>
            <div className="mt-2 flex items-center justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => revert.mutate(r.id)} loading={revert.isPending}>
                <RotateCcw className="size-3" />
                Revert to before
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
